import React from 'react';

import { Tooltip } from '@chakra-ui/react';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import { StackedCell } from 'components/DataDisplay';

dayjs.extend(duration);
dayjs.extend(LocalizedFormat);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

export const renderTime = ({ value }) => {
  if (value === '-') {
    return value;
  }

  if (value) {
    return (
      <Tooltip
        label={timeTooltipLabel(value)}
        hasArrow
        placement="top"
        borderRadius={6}
      >
        {timeLabel(value)}
      </Tooltip>
    );
  }

  return '-';
};

export const timeTooltipLabel = value =>
  dayjs.utc(value).local().format('LLLL');

export const formatTime = (value, format = 'DD-MMM-YY, hh:mm A') =>
  dayjs.utc(value).local().format(format);

export const timeLabel = value =>
  !!value ? dayjs.utc(value).local().format('DD-MMM-YY hh:mm A') : 'None';

export const dateLabel = value =>
  dayjs.utc(value).local().format('DD-MMM, YYYY');

export const timeSince = value => dayjs.utc(value).local().fromNow();

export const renderTimeSince = ({ value }) => {
  if (value === '-') {
    return value;
  }

  if (value) {
    return (
      <Tooltip
        label={timeTooltipLabel(value)}
        hasArrow
        placement="top"
        borderRadius={6}
      >
        {timeSince(value)}
      </Tooltip>
    );
  }

  return '-';
};

export const renderTimeStackedCell = ({ value }) => {
  if (!value || value === '-') return '-';

  // Removed an unreachable `if (value)` / `return '-'` (empty values return above).
  return (
    <StackedCell
      upper={dateLabel(value)}
      lower={dayjs.utc(value).local().format('hh:mm A')}
      showLowerTooltip={false}
      showUpperTooltip={false}
    />
  );
};

export const convertToMinutes = (value, unit) => {
  if (!value || Number.isNaN(value)) return 0;
  switch (unit) {
    case 'days':
      return value * 24 * 60;
    case 'hours':
      return value * 60;
    case 'minutes':
      return value;
    default:
      // Fixed: return 0 for unknown units instead of logging and returning
      // undefined, which turned callers' sums into NaN.
      return 0;
  }
};

export const convertMinutes = minutes => {
  const totalMinutes = Number(minutes);
  // Fixed: invalid input returns undefined quietly (no console.log).
  if (isNaN(totalMinutes) || totalMinutes < 0) return;

  const d = dayjs.duration(totalMinutes, 'minutes');
  const days = Math.floor(d.asDays());
  const hours = Math.floor(d.asHours() % 24);
  const mins = Math.floor(d.asMinutes() % 60);

  let formatted = '';
  if (days > 0) formatted += `${days} day${days > 1 ? 's' : ''} `;
  if (hours > 0) formatted += `${hours} hr${hours > 1 ? 's' : ''} `;
  if (mins > 0) formatted += `${mins} min${mins > 1 ? 's' : ''}`;

  return formatted.trim() || '0 min';
};

export const getTimeOfDay = () => {
  const hour = dayjs().hour();

  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  return 'evening';
};

// Fixed: build the greeting when it is shown. The old `greeting` constant was
// computed once at module load and went stale in a long-lived session.
export const getGreeting = () => `Good ${getTimeOfDay()}`;
